import { makeStyles, TextField } from '@material-ui/core';
import { Grid, Typography } from '@material-ui/core/';
import { withStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Edit,
  FormTab,
  TabbedForm,
  TopToolbar,
  List
} from 'react-admin';
import { FormSpy } from 'react-final-form';
import { connect } from 'react-redux';
import ViewTab from '../../core/components/forked/layout/ViewTab';
import EditToolbar from '../../core/components/edit/EditToolbar';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import FormTitleBar from '../../core/components/FormTitleBar';
import Empty from '../../core/components/list/Empty';
import { ListPagination } from '../../core/components/list/ListPagination';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import {
  getInitials,
  predictiveSearchFormatter,
  predictiveSearchParser,
  removePathEnd
} from '../../core/utils';
import AddExistingOrphanFundDialog from './fundfamiliesfund/AddExistingOrphanFundDialog';
import FundFamilyFundCreateDialog from './fundfamiliesfund/FundFamilyFundCreateDialog';
import FundFamilyFundDatagrid from './fundfamiliesfund/FundFamilyFundDatagrid';
import RemoveFundFamilyFundButton from './fundfamiliesfund/RemoveFundFamilyFundButton';
import FundFamilySummary from './FundFamilySummary';

const styles = () => ({
  noActions: {
    marginTop: '0px'
  }
});

const useStyles = makeStyles({
  headerContainer: {
    paddingTop: '8px'
  }
});

const FundActionStyles = {
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    marginLeft: '10px'
  }
};

const useFilterStyles = makeStyles({
  filterInput: {
    width: 'unset',
    alignSelf: 'center'
  }
});

const FundFilters = ({ setFilters, filterValues }) => {
  const [fundNameFilter, setFundNameFilter] = useState('');
  const classes = useFilterStyles();

  const handleNameFilterChange = event => {
    setFundNameFilter(event.target.value);
  };

  useEffect(() => {
    setFilters({
      ...filterValues,
      name: predictiveSearchParser(fundNameFilter)
    });
  }, [filterValues, fundNameFilter, setFilters]);

  return (
    <TextField
      className={classes.filterInput}
      onChange={handleNameFilterChange}
      label="Search by Fund Name"
      variant="standard"
      value={predictiveSearchFormatter(fundNameFilter)}
    />
  );
};

const FundFundFamilyAction = withStyles(FundActionStyles)(
  ({ classes, hasCreateFundPermission, hasEditFundPermission, ...props }) => (
    <TopToolbar>
      {hasCreateFundPermission && (
        <Button
          color="primary"
          onClick={props.handleFundCreateButtonClick}
          label="Add New Fund"
          className={classes.addButton}
          disableFocusRipple
        />
      )}
      {hasEditFundPermission && (
        <Button
          color="primary"
          onClick={props.handleAddExistingOrphanFundDialogToggle}
          label="Add Existing Fund"
          className={classes.addButton}
          disableFocusRipple
        />
      )}
    </TopToolbar>
  )
);

const FundFamilyTitle = ({ record }) => {
  return (
    <FormTitleBar
      recordLabel={'Fund Family ID: ' + record.id}
      name={record.name}
      status={record.status}
      statusLabel={'Status:'}
      showAvatar={true}
      avatarContent={getInitials(record.name)}
    />
  );
};

const FundFamilyRelatedFundsLabel = ({ label }) => {
  const classes = useStyles();

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.headerContainer}>
          {label}
        </Typography>
      </Grid>
    </Grid>
  );
};

class FundFamilyEdit extends React.Component {
  state = {
    showFundFamilyFundCreateDialog: false,
    showAddExistingOrphanFundDialog: false,
    whenBlockNavigation: false
  };

  handleFundFamilyFundCreateDialogToggle = () => {
    this.setState(prevState => {
      return {
        showFundFamilyFundCreateDialog: !prevState.showFundFamilyFundCreateDialog
      };
    });
  };

  handleAddExistingOrphanFundDialogToggle = () => {
    this.setState(prevState => {
      return {
        showAddExistingOrphanFundDialog: !prevState.showAddExistingOrphanFundDialog
      };
    });
  };

  onBeforeDelete = () => {
    // Unblock navigation before deleting
    this.setState({
      whenBlockNavigation: false
    });
  };

  handleFormIsDirty(dirty) {
    // Block navigation if the form is dirty
    this.setState({ whenBlockNavigation: dirty });
  }

  render() {
    const {
      showFundFamilyFundCreateDialog,
      showAddExistingOrphanFundDialog,
      whenBlockNavigation
    } = this.state;
    const fundFamilyId = this.props.id;
    const displayToolbar = this.props.match.isExact;
    const {
      classes,
      dispatch,
      permissions = [],
      hasCreatePermission,
      hasEditPermission,
      hasDeletePermission,
      ...rest
    } = this.props;
    const hasCreateFundPermission = permissions.includes('AUTH_CREATE_FUND');
    const hasEditFundPermission = permissions.includes('AUTH_EDIT_FUND');

    return (
      <React.Fragment>
        <NavigationPrompt
          when={whenBlockNavigation}
          adjustMatchingPath={removePathEnd}
          hasEditPermission={hasEditPermission}
        >
          {(isOpen, onConfirm, onCancel) => (
            <LeaveConfirm
              isOpen={isOpen}
              onConfirm={onConfirm}
              onClose={onCancel}
            />
          )}
        </NavigationPrompt>

        <Edit {...rest} undoable={false} title={<FundFamilyTitle />}>
          <TabbedForm
            toolbar={
              displayToolbar ? (
                <EditToolbar
                  {...rest}
                  hasEditPermission={hasEditPermission}
                  hasDeletePermission={hasDeletePermission}
                  onBeforeDelete={this.onBeforeDelete}
                />
              ) : (
                <></>
              )
            }
            variant="standard"
            margin="normal"
          >
            <FormTab label="Fund Family Details">
              <FundFamilySummary creation={false} />
              <FormSpy
                subscription={{ dirty: true }}
                onChange={props => {
                  this.handleFormIsDirty(props.dirty);
                }}
              />
            </FormTab>
            <ViewTab label="Related Funds">
              <FundFamilyRelatedFundsLabel label="Related Funds" />
              <List
                {...rest}
                filters={<FundFilters />}
                pagination={<ListPagination />}
                style={{ margin: '0px', width: '100%' }}
                resource="funds"
                filter={{ fundFamilyId }}
                sort={{ field: 'name', order: 'ASC' }}
                title=" "
                actions={
                  <FundFundFamilyAction
                    fundFamilyId={fundFamilyId}
                    handleFundCreateButtonClick={
                      this.handleFundFamilyFundCreateDialogToggle
                    }
                    handleAddExistingOrphanFundDialogToggle={
                      this.handleAddExistingOrphanFundDialogToggle
                    }
                    hasCreateFundPermission={hasCreateFundPermission}
                    hasEditFundPermission={hasEditFundPermission}
                  />
                }
                bulkActionButtons={
                  hasEditFundPermission && (
                    <RemoveFundFamilyFundButton
                      {...rest}
                      fundFamilyId={fundFamilyId}
                    />
                  )
                }
                empty={
                  <Empty
                    emptyText="There are no related Funds to be displayed"
                    buttons={[
                      {
                        label: 'Add New Fund',
                        onClick: this.handleFundFamilyFundCreateDialogToggle,
                        hide: !hasCreateFundPermission
                      },
                      {
                        label: 'Add Existing Fund',
                        onClick: this.handleAddExistingOrphanFundDialogToggle,
                        hide: !hasEditFundPermission
                      }
                    ]}
                  />
                }
              >
                <FundFamilyFundDatagrid permissions={permissions} {...rest} />
              </List>
              <FundFamilyFundCreateDialog
                title=" "
                showDialog={showFundFamilyFundCreateDialog}
                handleDialogToggle={this.handleFundFamilyFundCreateDialogToggle}
                {...rest}
              />
              <AddExistingOrphanFundDialog
                title=" "
                showDialog={showAddExistingOrphanFundDialog}
                handleDialogToggle={
                  this.handleAddExistingOrphanFundDialogToggle
                }
                fundFamilyId={fundFamilyId}
                {...rest}
              />
            </ViewTab>
          </TabbedForm>
        </Edit>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, props) => ({
  funds: state.admin.resources.funds // List of funds
});

export default connect(
  mapStateToProps,
  null
)(withStyles(styles)(FundFamilyEdit));
